import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const colors = [
  "#FABE7A", "#F6866A", "#59E6F6", "#7661E2",
  "rgba(250, 190, 122, 0.7)", "rgba(246, 134, 106, 0.7)", "rgba(89, 230, 246, 0.7)", "rgba(118, 97, 226, 0.7)",
  "#D89F6A", "#E4775A", "#49C6D6", "#6451B2",
];

const initialDatasetsConfig = [
  { id: "rc_awd", label: "RC_AWD", color: colors[0] },
  { id: "a_s_spinning", label: "A/S Spinning", color: colors[1] },
  { id: "a_s_reg_up", label: "A/S Regulation Up", color: colors[2] },
  { id: "a_s_non_spin", label: "A/S Non-Spinning", color: colors[3] },
  { id: "a_s_reg_down_mileage", label: "A/S Reg Down Mileage", color: colors[4] },
  { id: "a_s_reg_up_mileage", label: "A/S Reg Up Mileage", color: colors[5] },
  { id: "a_s_reg_down", label: "A/S Regulation Down", color: colors[6] },
  { id: "ramp_up", label: "Ramp Up", color: colors[7] },
  { id: "ramp_down", label: "Ramp Down", color: colors[8] },
  { id: "flexible_ramp_up", label: "Flexible Ramp Up", color: colors[9] },
  { id: "flexible_ramp_down", label: "Flexible Ramp Down", color: colors[10] },
  { id: "energy", label: "Energy", color: colors[11] },
  { id: "export", label: "Export", color: colors[0] },
  { id: "meter", label: "Meter", color: colors[1] },
  { id: "ard", label: "ARD", color: colors[2] },
  { id: "generation", label: "Generation", color: colors[3] },
  { id: "generation_award", label: "Generation Award", color: colors[4] },
  { id: "energy_bid", label: "Energy Bid", color: colors[5] },
  { id: "energy_only_offer", label: "Energy Only Offer", color: colors[6] },
  { id: "reliability_deployment_order", label: "Reliability Deployment Order", color: colors[7] },
];

const PriceTabChart: React.FC = () => {
  const [chartData, setChartData] = useState<any>(null);
  const [visibility, setVisibility] = useState<{ [key: string]: boolean }>(
    Object.fromEntries(initialDatasetsConfig.map(({ id }) => [id, true]))
  );

  useEffect(() => {
    const points = 50;
    const baseline = 50;
    const maxSpike = 200;

    const generateLineData = () => Array.from(
      { length: points },
      () => Math.random() > 0.9 ? baseline + Math.random() * maxSpike : baseline
    );

    const labels = Array.from(
      { length: points },
      (_, i) => `Day ${i + 1}`
    );

    const datasets = initialDatasetsConfig.map((config) => ({
      id: config.id,
      label: config.label,
      data: generateLineData(),
      borderColor: config.color,
      backgroundColor: `${config.color}1A`,
      borderWidth: 1.5,
      pointRadius: 0,
      tension: 0.3,
    }));

    setChartData({ labels, datasets });
  }, []);

  const toggleVisibility = (id: string) => {
    setVisibility((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const filteredData = chartData && {
    ...chartData,
    datasets: chartData.datasets.filter((dataset: any) => visibility[dataset.id]),
  };

  return (
    <div className="w-full mx-auto p-4">
      {filteredData && (
        <div className="relative w-full h-[300px] sm:h-[500px]">
          <Line
            data={filteredData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                x: { title: { display: true, text: "Days" } },
                y: { title: { display: true, text: "Values" } },
              },
              plugins: { legend: { display: false } },
            }}
          />
        </div>
      )}
      {/* Legend Section */}
      <div className="flex flex-wrap gap-8 justify-between mt-8 mb-8 sm:flex-row flex-col">
        {/* Ancillary Services Legend */}
        <div className="flex flex-col border rounded-lg p-4 flex-1 bg-[#F6F6F6]">
          <h4 className="text-lg text-gray-600 font-semibold mb-4">
            Ancillary Services
          </h4>
          <div className="grid grid-cols-2 gap-4 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-400">
            {initialDatasetsConfig
              .filter((dataset) =>
                [
                  "rc_awd",
                  "a_s_spinning",
                  "a_s_reg_up",
                  "a_s_non_spin",
                  "a_s_reg_down_mileage",
                  "a_s_reg_up_mileage",
                  "a_s_reg_down",
                  "ramp_up",
                  "ramp_down",
                  "flexible_ramp_up",
                  "flexible_ramp_down",
                ].includes(dataset.id)
              )
              .map((dataset) => (
                <label key={dataset.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!visibility[dataset.id]} 
                    onChange={() => toggleVisibility(dataset.id)}
                    className="peer w-4 h-4 accent-violet-500"
                  />
                  <span className="flex items-center text-sm text-gray-600">
                    <span
                      className="inline-block w-4 h-[2px] mr-2"
                      style={{ backgroundColor: dataset.color }}
                    />
                    {dataset.label}
                  </span>
                </label>
              ))}
          </div>
        </div>

        {/* Energy Legend */}
        <div className="flex flex-col border rounded-lg p-4 flex-1 bg-[#F6F6F6]">
          <h4 className="text-lg text-gray-600 font-semibold mb-4">Energy</h4>
          <div className="grid grid-cols-2 gap-4 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-400">
            {initialDatasetsConfig
              .filter((dataset) =>
                [
                  "energy",
                  "export",
                  "meter",
                  "ard",
                  "generation",
                  "generation_award",
                  "energy_bid",
                  "energy_only_offer",
                  "reliability_deployment_order",
                ].includes(dataset.id)
              )
              .map((dataset) => (
                <label key={dataset.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!visibility[dataset.id]} 
                    onChange={() => toggleVisibility(dataset.id)}
                    className="peer w-4 h-4 accent-violet-500"
                  />
                  <span className="flex items-center text-sm text-gray-600">
                    <span
                      className="inline-block w-4 h-[2px] mr-2"
                      style={{ backgroundColor: dataset.color }}
                    />
                    {dataset.label}
                  </span>
                </label>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceTabChart
